/**
 * XP System - Experience Points Calculation
 *
 * Calculates XP rewards based on:
 * - Question difficulty (IRT beta/theta)
 * - Speed bonus (elapsed answer time)
 * - Streak bonus (consecutive correct answers)
 * - Partial credit (2nd/3rd attempts)
 */

// =============================================================================
// Configuration (easy to tweak later)
// =============================================================================

const XP_CONFIG = {
  // Base XP from question difficulty
  baseMin: 5,
  baseMax: 100,
  betaMin: -3, // IRT theta minimum
  betaMax: 3, // IRT theta maximum

  // Speed bonus - grace period then linear decay
  speedBonusGracePeriod: 6, // Full bonus for first 6 seconds
  speedBonusDecayEnd: 15, // Bonus reaches 0 at 15 seconds
  speedMaxBonus: 10,

  // Streak bonus
  streakMultiplier: 2,
  streakMaxBonus: 20,

  // Haste penalty - logarithmic decay within penalty window
  hastePenaltyWindow: 4.2, // Strict penalty window (4.2 seconds) - used when previous answer was hasty
  relaxedHasteWindow: 2.2, // Relaxed penalty window (2.2 seconds) - used when previous answer was not hasty
  hasteMaxPenalty: 100, // Max penalty for instant answer

  partialCredit: {
    secondAttempt: 0.2,
    thirdAttempt: 0.05,
  },

  // Rating bonus
  ratingBonus: 10,
} as const;

// =============================================================================
// Types
// =============================================================================

export interface XPCalculation {
  baseXP: number;
  correctXP: number; // Actual XP for "Correct" (baseXP × partialMultiplier)
  speedBonus: number;
  streakBonus: number;
  hastePenalty: number; // Penalty for quick incorrect answers
  ratingBonus?: number; // Bonus for rating the question
  partialMultiplier: number;
  total: number;
  focusPenaltyApplied: boolean; // Whether focus penalty was applied
}

export type AttemptNumber = 1 | 2 | 3 | 4;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Clamps a value between min and max
 */
const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

/**
 * Calculates base XP from question difficulty (beta/theta)
 * Maps beta range [-3, +3] to XP range [5, 100]
 */
export const calculateBaseXP = (beta: number): number => {
  const { baseMin, baseMax, betaMin, betaMax } = XP_CONFIG;
  const range = baseMax - baseMin;
  const betaRange = betaMax - betaMin;

  // Normalize beta to 0-1 range
  const normalized = (clamp(beta, betaMin, betaMax) - betaMin) / betaRange;

  // Map to XP range and round
  return Math.round(baseMin + normalized * range);
};

/**
 * Calculates speed bonus based on elapsed time since question appeared.
 * - First 6 seconds: Full bonus (10 points)
 * - 6-15 seconds: Linear decay from 10 to 0
 * - After 15 seconds: No bonus
 * @param elapsedSeconds - Seconds since question was displayed
 */
export const calculateSpeedBonus = (elapsedSeconds: number): number => {
  const { speedBonusGracePeriod, speedBonusDecayEnd, speedMaxBonus } = XP_CONFIG;

  if (elapsedSeconds <= speedBonusGracePeriod) {
    return speedMaxBonus; // Full bonus during grace period
  }

  if (elapsedSeconds >= speedBonusDecayEnd) {
    return 0; // No bonus after decay period
  }

  // Linear decay from grace period to zero
  const decayDuration = speedBonusDecayEnd - speedBonusGracePeriod;
  const elapsedInDecay = elapsedSeconds - speedBonusGracePeriod;
  return Math.round(speedMaxBonus * (1 - elapsedInDecay / decayDuration));
};

/**
 * Threshold in seconds to determine if an answer was "hasty".
 * If the user answered faster than this, the next question uses strict penalty mode.
 */
export const HASTE_THRESHOLD_SECONDS = XP_CONFIG.hastePenaltyWindow;

/**
 * Calculates haste penalty for incorrect answers made too quickly.
 * Uses logarithmic decay - penalty drops quickly at first, then slows.
 *
 * **Strict Mode** (isStrict=true, previous answer was hasty):
 * - At 0 seconds: max penalty (80 points)
 * - At 5 seconds: 0 points (no penalty)
 *
 * **Relaxed Mode** (isStrict=false, previous answer was not hasty):
 * - At 0 seconds: max penalty (80 points)
 * - At 2 seconds: 0 points (no penalty)
 * - Decays much faster, giving benefit of the doubt
 *
 * @param elapsedSeconds - Seconds since question was displayed
 * @param isStrict - Whether to use strict (5s) or relaxed (2s) penalty window
 */
export const calculateHastePenalty = (elapsedSeconds: number, isStrict: boolean = true): number => {
  const { hastePenaltyWindow, relaxedHasteWindow, hasteMaxPenalty } = XP_CONFIG;
  const penaltyWindow = isStrict ? hastePenaltyWindow : relaxedHasteWindow;

  if (elapsedSeconds >= penaltyWindow) {
    return 0; // No penalty after window
  }

  if (elapsedSeconds <= 0) {
    return hasteMaxPenalty; // Max penalty for instant guess
  }

  // Logarithmic decay: penalty drops quickly at first, then slows
  // ln(1 + t) / ln(1 + window) gives 0→1 log curve
  const logProgress = Math.log(1 + elapsedSeconds) / Math.log(1 + penaltyWindow);
  return Math.round(hasteMaxPenalty * (1 - logProgress));
};

/**
 * Calculates streak bonus based on current streak
 * @param currentStreak - Number of consecutive correct answers
 */
export const calculateStreakBonus = (currentStreak: number): number => {
  const { streakMultiplier, streakMaxBonus } = XP_CONFIG;
  return Math.min(streakMaxBonus, currentStreak * streakMultiplier);
};

/**
 * Gets the partial credit multiplier for a given attempt number
 */
const getPartialCreditMultiplier = (attemptNumber: AttemptNumber): number => {
  switch (attemptNumber) {
    case 1:
      return 1;
    case 2:
      return XP_CONFIG.partialCredit.secondAttempt;
    case 3:
      return XP_CONFIG.partialCredit.thirdAttempt;
    case 4:
    default:
      return 0; // Wrong answer (4th attempt = exhausted all options)
  }
};

/**
 * Main XP calculation function
 *
 * @param beta - Question difficulty theta (typically -3 to +3)
 * @param elapsedSeconds - Seconds since question was displayed
 * @param currentStreak - Consecutive correct answers (0 = no streak)
 * @param attemptNumber - Which attempt this correct answer was on (1-4)
 * @param isIncorrect - Whether this is calculating for an incorrect answer (applies penalty)
 * @param focusLost - Whether focus was lost during question (anti-cheat penalty)
 * @param isStrictPenalty - Whether to use strict (5s) or relaxed (2s) haste penalty window
 * @returns XP breakdown and total
 */
export const calculateXP = (
  beta: number,
  elapsedSeconds: number,
  currentStreak: number,
  attemptNumber: AttemptNumber = 1,
  isIncorrect: boolean = false,
  focusLost: boolean = false,
  isStrictPenalty: boolean = true
): XPCalculation => {
  const baseXP = calculateBaseXP(beta);
  const partialMultiplier = getPartialCreditMultiplier(attemptNumber);

  // Apply focus penalty: 10% of normal correctXP
  const focusPenaltyApplied = focusLost && !isIncorrect;
  const correctXP = focusPenaltyApplied
    ? Math.round(baseXP * partialMultiplier * 0.1)
    : Math.round(baseXP * partialMultiplier);

  // Haste penalty for incorrect answers (logarithmic decay)
  // Uses strict (5s) or relaxed (2s) window based on previous answer behavior
  const hastePenalty = isIncorrect ? calculateHastePenalty(elapsedSeconds, isStrictPenalty) : 0;

  // Only first attempt gets speed and streak bonuses (and only on correct answers)
  // Focus penalty zeroes out all bonuses
  const isFirstAttempt = attemptNumber === 1;
  const speedBonus =
    isFirstAttempt && !isIncorrect && !focusLost ? calculateSpeedBonus(elapsedSeconds) : 0;
  const streakBonus =
    isFirstAttempt && !isIncorrect && !focusLost ? calculateStreakBonus(currentStreak) : 0;

  // Calculate total: penalty is negative XP, bonuses are positive
  // Use explicit 0 to avoid -0 when hastePenalty is 0
  const total = isIncorrect
    ? hastePenalty > 0
      ? -hastePenalty
      : 0
    : isFirstAttempt
      ? (focusPenaltyApplied ? correctXP : baseXP) + speedBonus + streakBonus
      : correctXP;

  return {
    baseXP,
    correctXP,
    speedBonus,
    streakBonus,
    hastePenalty,
    partialMultiplier,
    total,
    focusPenaltyApplied,
  };
};

// =============================================================================
// Display Formatting
// =============================================================================

// =============================================================================
// Level Calculation
// =============================================================================

export interface XPLevelInfo {
  level: number;
  currentXP: number;
  nextLevelXP: number;
  progressPct: number;
  xpToNextLevel: number;
}

/**
 * Calculates the canonical XP level from total XP.
 * Levels are dynamic milestones derived from total_xp; no denormalized user_level column.
 * Negative total XP is clamped to Level 1 with 0% progress.
 * Formula: first threshold 800, then threshold_n = threshold_{n-1} * 1.2 + 1000.
 */
export const calculateXPLevel = (totalXP: number): XPLevelInfo => {
  let effectiveXP = Math.max(0, totalXP);
  let level = 1;
  let threshold = 800;

  while (effectiveXP >= threshold) {
    effectiveXP -= threshold;
    level++;
    threshold = Math.floor(threshold * 1.2 + 1000);
  }

  return {
    level,
    currentXP: effectiveXP,
    nextLevelXP: threshold,
    progressPct: Math.round((effectiveXP / threshold) * 100),
    xpToNextLevel: threshold - effectiveXP,
  };
};

/**
 * Formats XP for display with appropriate abbreviations
 *
 * - Under 1,000: Show full number (e.g., "523")
 * - 1,000 - 999,999: Show as X.Xk (e.g., "1.2k", "99.5k")
 * - 1,000,000+: Show as X.XXM (e.g., "1.23M", "92.32M")
 */
export const formatXP = (xp: number): string => {
  if (xp >= 1_000_000) {
    return `${(xp / 1_000_000).toFixed(2)}M`;
  }
  if (xp >= 1_000) {
    return `${(xp / 1_000).toFixed(1)}k`;
  }
  return xp.toString();
};
